import { useMemo, useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ShoppingBag, ArrowRight, Check, Star } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useProducts } from '../context/ProductsContext';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import { buildBreadcrumbJsonLd } from '../lib/seoSchema';
import { brands, brandBySlug } from '@shared/brands';
import NotFoundPage from './NotFoundPage';

export default function BrandPage() {
  const { pathname } = useLocation();
  const slug = pathname.slice(1);
  const brand = brandBySlug[slug];
  const { isDark } = useTheme();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => { setOpenFaq(null); }, [slug]);

  const { products } = useProducts();
  const relatedBrands = useMemo(() => brands.filter((b) => b.slug !== slug), [slug]);

  const heroProduct = useMemo(() => {
    const brandProducts = products.filter((p) => p.brand === brand?.name);
    return (
      brandProducts.find((p) => p.bestSeller) ??
      brandProducts.find((p) => p.featured) ??
      brandProducts[0] ??
      null
    );
  }, [products, brand]);

  if (!brand) return <NotFoundPage />;

  const breadcrumbItems = [
    { name: 'Home', url: '/' },
    { name: brand.name, url: `/${brand.slug}` },
  ];

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: brand.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <SEO
        title={brand.seoTitle ?? `Buy ${brand.name} Nicotine Pouches in India | ZeroSmoke.in`}
        description={brand.seoDescription ?? brand.description}
        canonical={`/${brand.slug}`}
        jsonLd={[buildBreadcrumbJsonLd(breadcrumbItems), faqJsonLd]}
      />

      <Breadcrumb items={[{ name: 'Home', href: '/' }, { name: brand.name }]} />

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className={`relative overflow-hidden border-b ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
        {/* Subtle left emerald glow */}
        <div className="absolute -left-32 top-0 h-full w-64 bg-emerald-500/5 blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-16">

            {/* Left — text */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="flex-1 min-w-0"
            >
              {/* Pill */}
              <span className={`inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-5 ${
                isDark
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                  : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              }`}>
                Nicotine Pouches
              </span>

              {/* Brand name */}
              <h1 className={`text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-none mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {brand.name}
              </h1>

              {/* Tagline */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-0.5 bg-emerald-500 rounded-full" />
                <p className={`text-base sm:text-lg font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                  {brand.tagline}
                </p>
              </div>

              <p className={`text-sm sm:text-base leading-relaxed text-justify mb-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {brand.seoIntro}
              </p>

              {/* CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.4 }}
                className="flex flex-wrap gap-3"
              >
                <Link
                  to={`/store?brand=${encodeURIComponent(brand.name)}`}
                  className="group inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl transition-all hover:shadow-lg hover:shadow-emerald-600/25 active:scale-[0.97] text-sm"
                >
                  <ShoppingBag size={16} />
                  Shop {brand.name}
                  <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                </Link>
                <Link
                  to="/store"
                  className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold border text-sm transition-all ${
                    isDark
                      ? 'border-gray-700 text-gray-300 hover:border-emerald-600 hover:text-emerald-400 hover:bg-emerald-500/5'
                      : 'border-gray-300 text-gray-700 hover:border-emerald-500 hover:text-emerald-600'
                  }`}
                >
                  All Brands
                </Link>
              </motion.div>
            </motion.div>

            {/* Right — hero product visual */}
            {heroProduct && (
              <motion.div
                initial={{ opacity: 0, scale: 0.88 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex-shrink-0 flex flex-col items-center"
              >
                <Link
                  to={`/product/${heroProduct.id}`}
                  className="group relative flex flex-col items-center"
                >
                  {/* Glow ring */}
                  <div className="absolute inset-0 rounded-full bg-emerald-500/10 blur-2xl scale-110 pointer-events-none" />

                  {/* Product can */}
                  <motion.img
                    src={heroProduct.image}
                    alt={heroProduct.name}
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                    className="relative z-10 w-44 h-44 sm:w-56 sm:h-56 object-contain drop-shadow-2xl"
                  />

                  {/* Best seller badge */}
                  {heroProduct.bestSeller && (
                    <span className="relative z-10 mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-600 text-white shadow-md">
                      <Star size={11} className="fill-white" />
                      Best Seller
                    </span>
                  )}

                  {/* Product name */}
                  <p className={`relative z-10 mt-2 text-sm font-medium text-center max-w-[180px] group-hover:text-emerald-500 transition-colors ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {heroProduct.name}
                  </p>
                </Link>
              </motion.div>
            )}

          </div>
        </div>
      </section>

      {/* ── FEATURES ROW ─────────────────────────────────── */}
      <section className={`border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {brand.features.map((feat, i) => (
              <motion.div
                key={feat}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.07, duration: 0.4 }}
                className={`flex items-start gap-3 px-4 py-3 rounded-xl ${
                  isDark ? 'bg-gray-900' : 'bg-white'
                }`}
              >
                <Check size={15} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                <span className={`text-sm leading-snug ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{feat}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT ────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-10">
        <section>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}
          >
            About {brand.name}
          </motion.h2>
          <div className="space-y-4 max-w-4xl">
            {brand.seoBody.map((para, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className={`text-sm sm:text-base leading-relaxed text-justify ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
              >
                {para}
              </motion.p>
            ))}
          </div>
        </section>
      </div>

      {/* ── FAQ ──────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16">
        <section>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`text-2xl font-bold mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`}
          >
            Frequently Asked Questions
          </motion.h2>
          <div className={`rounded-2xl border overflow-hidden max-w-4xl ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            {brand.faqs.map((faq, i) => (
              <motion.div
                key={faq.q}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className={`border-b last:border-b-0 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className={`w-full flex items-center justify-between gap-3 px-5 py-4 text-left transition-colors ${
                    isDark ? 'hover:bg-gray-800/60' : 'hover:bg-gray-50'
                  } ${openFaq === i ? (isDark ? 'bg-gray-800/40' : 'bg-gray-50') : ''}`}
                >
                  <h3 className={`text-sm font-semibold leading-snug ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {faq.q}
                  </h3>
                  <motion.span
                    animate={{ rotate: openFaq === i ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex-shrink-0"
                  >
                    <ChevronDown size={16} className={isDark ? 'text-emerald-400' : 'text-emerald-600'} />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {openFaq === i && (
                    <motion.div
                      key="a"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.22 }}
                      className="overflow-hidden"
                    >
                      <p className={`px-5 pb-4 text-sm leading-relaxed text-justify ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {faq.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </section>
      </div>

      {/* ── RELATED BRANDS ───────────────────────────────── */}
      <section className={`border-t ${isDark ? 'border-gray-800 bg-gray-900/40' : 'border-gray-200 bg-gray-100/60'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h2 className={`text-lg font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Explore Other Brands
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {relatedBrands.map((b, i) => (
              <motion.div
                key={b.slug}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
              >
                <Link
                  to={`/${b.slug}`}
                  className={`group block rounded-xl border p-4 transition-all duration-200 ${
                    isDark
                      ? 'bg-gray-900 border-gray-800 hover:border-emerald-600 hover:bg-gray-800'
                      : 'bg-white border-gray-200 hover:border-emerald-400'
                  }`}
                >
                  <p className={`font-bold text-sm mb-1 group-hover:text-emerald-500 transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {b.name}
                  </p>
                  <p className={`text-xs leading-snug ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    {b.tagline}
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
